import React from 'react';

import { Modal } from '../../../../common/display/modal/Modal';
import { Benefit } from '../../../../../@types/benefits';
import type { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import OpportunityCheckPng from './opportunity-check.png';
import OpportunityCheckWebp from './opportunity-check.webp';
import ArrowMarker from './arrow-marker.svg';
import ListingDemandPng from './anzeigennachfrage.png';
import ListingDemandWebp from './anzeigennachfrage.webp';
import ProspectAnalysisPng from './interessentinnenanalyse.png';
import ProspectAnalysisWebp from './interessentinnenanalyse.webp';
import { ImageSource } from '../../../../common/display/imageSource/ImageSource';
import { FAQTab } from '../../../../common/display/faqTab/FAQTab';
import { useGetHasProduct } from '../../../../../customHooks/useGetHasProduct.hook';
import { ProductType } from '../../../../../@types/product';
import { useGetDiscoverKaeuferPlusLink } from '../../../../../customHooks/useGetDiscoverKaeuferPlusLink.hook';
import { Msg, useIntl } from '@/i18n';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';

const styles = {
  overviewTab: { maxWidth: 530 },
  howItWorksTab: { maxWidth: 712 },
  howItWorksImg: {
    width: '100%',
    height: 'auto',
  },
};

const OverviewTab = ({ hasKaeuferPlus }: { hasKaeuferPlus: boolean }) => {
  return (
    <div style={styles.overviewTab}>
      <div className='padding-bottom-l font-heading-xsmall-regular'>
        <Msg id='benefits.opportunityCheck.modal.overview.paragraph1' defaultMessage='Good prospects? We show you...' />
      </div>
      <div>
        <Msg
          id='benefits.opportunityCheck.modal.overview.paragraph2'
          defaultMessage='How many people are really interested?...'
        />
      </div>
      {!hasKaeuferPlus && (
        <ul className='list-check list-spacing-s padding-top-l'>
          <li data-testid='kaeufer-plus-label-with-checkmark'>
            <Msg
              id='benefits.exclusiveListings.modal.overview.activeKaeuferPlus'
              defaultMessage='Keep Käufer Plus...'
              values={{
                BuyerPlusLogo: <PlusBadge variant='buy' />,
              }}
            />
          </li>
        </ul>
      )}
    </div>
  );
};

const HowItWorksTab = () => {
  const { t } = useIntl();

  return (
    <div style={styles.howItWorksTab}>
      <div className='font-heading-xsmall-regular padding-bottom-xxl'>
        <Msg id='benefits.opportunityCheck.modal.howItWorks.paragraph' defaultMessage='With the opportunity check...' />
      </div>
      <div className='grid grid-flex gutter-vertical gutter-horizontal-s grid-align-center'>
        <div className='grid-item palm-one-whole one-half padding-horizontal-xl'>
          <ImageSource
            image={ListingDemandPng}
            imageWebp={ListingDemandWebp}
            imageAlt={t({
              id: 'benefits.opportunityCheck.modal.howItWorks.section1.image',
              defaultMessage: 'Listing demand on mobile phone',
            })}
            sizes='(min-width: 669px) 296px, calc(100vw-112px)'
            style={styles.howItWorksImg}
          />
        </div>
        <div className='grid-item palm-one-whole one-half palm-padding-bottom-l'>
          <h5>
            <Msg id='benefits.opportunityCheck.modal.howItWorks.section1.title' defaultMessage='Listing demand' />
          </h5>
          <div>
            <Msg
              id='benefits.opportunityCheck.modal.howItWorks.section1.paragraph'
              defaultMessage='We show you how long ...'
            />
          </div>
        </div>
        <div className='grid-item palm-one-whole one-half palm-order-default order-one-down padding-horizontal-xl'>
          <ImageSource
            image={ProspectAnalysisPng}
            imageWebp={ProspectAnalysisWebp}
            imageAlt={t({
              id: 'benefits.opportunityCheck.modal.howItWorks.section2.image',
              defaultMessage: 'Prospect analysis on mobile phone',
            })}
            sizes='(min-width: 669px) 296px, calc(100vw-112px)'
            style={styles.howItWorksImg}
          />
        </div>
        <div className='grid-item palm-one-whole one-half'>
          <h5>
            <Msg id='benefits.opportunityCheck.modal.howItWorks.section2.title' defaultMessage='Prospect analysis' />
          </h5>
          <div>
            <Msg
              id='benefits.opportunityCheck.modal.howItWorks.section2.paragraph'
              defaultMessage='We give you information about your competition...'
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ButtonArea = () => {
  const discoverKaeuferPlusLink = useGetDiscoverKaeuferPlusLink({ ctaType: 'opportunityCheck' });
  return (
    <div className='align-center'>
      <a className='button-secondary button-rounded' href={discoverKaeuferPlusLink}>
        <Msg id='benefits.exclusiveListings.modal.linkButton' defaultMessage='Discover KäuferPlus' />
      </a>
    </div>
  );
};

export const OpportunityCheck = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const hasKaeuferPlus = useGetHasProduct(ProductType.KaeuferPlus);

  return (
    <Modal
      title={t({ id: 'benefits.opportunityCheck.headline', defaultMessage: 'Opportunity check' })}
      isVisible={isVisible}
      image={OpportunityCheckPng}
      imageWebp={OpportunityCheckWebp}
      imageUseFullWidth={false}
      imageAlt={t({
        id: 'benefits.opportunityCheck.modal.image',
        defaultMessage: 'phone with opportunity check preview',
      })}
      imageBackgroundStyles={{
        '@media (min-width: 669px)': {
          backgroundImage: `url(${ArrowMarker})`,
          backgroundSize: 'auto 280px',
          backgroundPosition: 'center bottom, 55% center',
        },
      }}
      tabsContent={[
        <OverviewTab key='benefits.opportunityCheck.modal.overview' hasKaeuferPlus={hasKaeuferPlus} />,
        <HowItWorksTab key='benefits.opportunityCheck.modal.howItWorks' />,
        <FAQTab key='benefits.opportunityCheck.modal.faq' benefit='opportunityCheck' />,
      ]}
      benefit={Benefit.OPPORTUNITY_CHECK}
      buttonArea={hasKaeuferPlus ? null : <ButtonArea />}
      onClose={onClose}
    />
  );
};
